import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';
import { 
  GraduationCap, User, CreditCard, Calendar, FileText, LogOut, Loader2,
  Bell, IndianRupee, Bus, Home, BookOpen, ClipboardList, Award,
  Clock, CheckCircle2, AlertCircle, Lock, ChevronRight, Laptop, MessageSquare, TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentDashboard() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [studentInfo, setStudentInfo] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [feeSummary, setFeeSummary] = useState(null);
  const [attendanceSummary, setAttendanceSummary] = useState(null);

  // Get tenant code from URL or localStorage
  const getTenantCode = () => {
    const pathParts = window.location.pathname.split('/');
    if (pathParts.length > 2 && pathParts[2] === 'student') {
      return pathParts[1];
    }
    return localStorage.getItem('student_tenant_code') || '';
  };
  
  const tenantCode = getTenantCode();
  const basePath = tenantCode ? `/${tenantCode}` : '';

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    const info = localStorage.getItem('studentInfo');
    
    if (!token) {
      navigate(`${basePath}/student/login`);
      return;
    }
    
    if (info) {
      const parsedInfo = JSON.parse(info);
      setStudentInfo(parsedInfo);
      
      // Check if student is withdrawn - redirect to withdrawn info page
      if (parsedInfo.status === 'Withdrawn') {
        navigate(`${basePath}/student/withdrawn`);
        return;
      }
    }
    
    fetchAllData();
  }, [navigate, basePath]);

  const fetchAllData = async () => {
    const token = localStorage.getItem('studentToken');
    
    try {
      // Fetch dashboard and notifications first
      const dashboardRes = await fetch(`${API_URL}/api/student-portal/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setDashboardData(data);
        
        // Check if student is withdrawn - redirect to withdrawn info page
        if (data.status === 'Withdrawn') {
          // Update local storage
          const info = localStorage.getItem('studentInfo');
          if (info) {
            const parsedInfo = JSON.parse(info);
            parsedInfo.status = 'Withdrawn';
            localStorage.setItem('studentInfo', JSON.stringify(parsedInfo));
          }
          navigate('/student/withdrawn');
          return;
        }
        
        // Only fetch fee and attendance for admitted students
        if (data.status === 'admitted') {
          const [notifRes, feeRes, attendanceRes] = await Promise.all([
            fetch(`${API_URL}/api/student-portal/notifications`, {
              headers: { 'Authorization': `Bearer ${token}` }
            }),
            fetch(`${API_URL}/api/student-portal/fees/summary`, {
              headers: { 'Authorization': `Bearer ${token}` }
            }),
            fetch(`${API_URL}/api/student-portal/attendance`, {
              headers: { 'Authorization': `Bearer ${token}` }
            })
          ]);
          
          if (notifRes.ok) {
            const notifData = await notifRes.json();
            console.log('Notification response:', notifData);  // Debug log
            setNotifications(notifData.notifications || []);
            setUnreadCount(notifData.unread_count || 0);
          } else {
            console.error('Notification fetch failed:', notifRes.status);
          }
          
          if (feeRes.ok) {
            const feeData = await feeRes.json();
            setFeeSummary(feeData);
          }
          
          if (attendanceRes.ok) {
            const attData = await attendanceRes.json();
            setAttendanceSummary(attData.summary);
          }
        } else {
          // For non-admitted, just fetch notifications
          const notifRes = await fetch(`${API_URL}/api/student-portal/notifications`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (notifRes.ok) {
            const notifData = await notifRes.json();
            console.log('Notification response (non-admitted):', notifData);
            setNotifications(notifData.notifications || []);
            setUnreadCount(notifData.unread_count || 0);
          }
        }
      } else {
        console.error('Dashboard fetch failed:', dashboardRes.status);
      }
      
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    const tenantCode = localStorage.getItem('student_tenant_code') || localStorage.getItem('tenant_code');
    localStorage.removeItem('studentToken');
    localStorage.removeItem('studentInfo');
    localStorage.removeItem('student_tenant_code');
    toast.success('Logged out successfully');
    // Stay on tenant-specific student login page
    if (tenantCode) {
      window.location.href = `/${tenantCode}/student/login`;
    } else {
      navigate('/student/login');
    }
  };

  const markNotificationRead = async (notifId) => {
    const token = localStorage.getItem('studentToken');
    try {
      await fetch(`${API_URL}/api/student-portal/notifications/${notifId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => 
        n.id === notifId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'booked':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
            <Clock className="w-4 h-4" /> Form Pending
          </span>
        );
      case 'form_submitted':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
            <AlertCircle className="w-4 h-4" /> Awaiting Approval
          </span>
        );
      case 'admitted':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
            <CheckCircle2 className="w-4 h-4" /> Admitted
          </span>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const status = dashboardData?.status || studentInfo?.status;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Student Portal</h1>
                <p className="text-sm text-gray-500">{dashboardData?.student_id || studentInfo?.student_id}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Notification Bell */}
              <div className="relative">
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-full"
                >
                  <Bell className="w-6 h-6" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notification Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50">
                    <div className="p-3 border-b flex justify-between items-center">
                      <h3 className="font-semibold">Notifications</h3>
                      <button 
                        onClick={() => setShowNotifications(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >×</button>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="p-4 text-center text-gray-500">No notifications</p>
                      ) : (
                        notifications.slice(0, 5).map((notif) => (
                          <div 
                            key={notif.id}
                            onClick={() => markNotificationRead(notif.id)}
                            className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${!notif.is_read ? 'bg-blue-50' : ''}`}
                          >
                            <div className="flex items-start gap-2">
                              {notif.priority === 'high' && <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />}
                              <div className="flex-1">
                                <p className="font-medium text-sm">{notif.title}</p>
                                <p className="text-xs text-gray-600 mt-1">{notif.message}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {formatDate(notif.created_at)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <Button variant="ghost" onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Welcome Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-indigo-100 flex items-center justify-center">
                  {(dashboardData?.photo_url || dashboardData?.profile_photo) ? (
                    <img 
                      src={dashboardData?.photo_url || dashboardData?.profile_photo} 
                      alt={dashboardData?.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-8 h-8 text-indigo-600" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Welcome, {dashboardData?.name || studentInfo?.student_name}!
                  </h2>
                  <p className="text-gray-600">{dashboardData?.course} • {dashboardData?.session}</p>
                </div>
              </div>
              <div>{getStatusBadge(status)}</div>
            </div>
          </CardContent>
        </Card>

        {/* Status-based content */}
        {status === 'booked' && (
          <Card className="mb-6 border-yellow-200 bg-yellow-50">
            <CardHeader>
              <CardTitle className="text-yellow-800">Complete Your Admission Form</CardTitle>
              <CardDescription className="text-yellow-700">
                Your seat has been booked. Please complete the admission form with your details.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/student/form-fill')} className="bg-yellow-600 hover:bg-yellow-700">
                <FileText className="w-4 h-4 mr-2" /> Fill Admission Form
              </Button>
            </CardContent>
          </Card>
        )}

        {status === 'form_submitted' && (
          <Card className="mb-6 border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="text-blue-800">Form Submitted Successfully</CardTitle>
              <CardDescription className="text-blue-700">
                Your form has been submitted. You will get full access once it is approved by the college.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* Main Dashboard - Only for admitted students */}
        {status === 'admitted' && (
          <>
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">Fee Due</p>
                      <p className="text-2xl font-bold">₹{(feeSummary?.due || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <IndianRupee className="w-10 h-10 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">Attendance</p>
                      <p className="text-2xl font-bold">{attendanceSummary?.percentage || 0}%</p>
                    </div>
                    <Calendar className="w-10 h-10 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">Present</p>
                      <p className="text-2xl font-bold">{attendanceSummary?.present || 0} days</p>
                    </div>
                    <CheckCircle2 className="w-10 h-10 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">Notifications</p>
                      <p className="text-2xl font-bold">{unreadCount}</p>
                    </div>
                    <Bell className="w-10 h-10 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Menu Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Fee Section */}
              <Card data-testid="fee-details-card" className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/student/fees')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <IndianRupee className="w-6 h-6 text-green-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">Fee Details</CardTitle>
                  <CardDescription>View fee ledger, installments & pay online</CardDescription>
                </CardHeader>
              </Card>

              {/* Attendance */}
              <Card data-testid="attendance-card" className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/student/attendance')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-blue-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">Attendance</CardTitle>
                  <CardDescription>View your attendance report</CardDescription>
                </CardHeader>
              </Card>

              {/* Results */}
              <Card data-testid="results-card" className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/student/results')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Award className="w-6 h-6 text-purple-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">Exam Results</CardTitle>
                  <CardDescription>View your exam results</CardDescription>
                </CardHeader>
              </Card>

              {/* Routine */}
              <Card data-testid="routine-card" className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/student/routine')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <ClipboardList className="w-6 h-6 text-indigo-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">Class Routine</CardTitle>
                  <CardDescription>View your class timetable</CardDescription>
                </CardHeader>
              </Card>

              {/* Hostel */}
              <Card data-testid="hostel-card" className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/student/hostel')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center">
                      <Home className="w-6 h-6 text-pink-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">Hostel</CardTitle>
                  <CardDescription>Hostel fee & ledger</CardDescription>
                </CardHeader>
              </Card>

              {/* Transport */}
              <Card data-testid="transport-card" className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/student/transport')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <Bus className="w-6 h-6 text-yellow-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">Transport</CardTitle>
                  <CardDescription>Transport fee & ledger</CardDescription>
                </CardHeader>
              </Card>

              {/* Library */}
              <Card data-testid="library-card" className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/student/library')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-teal-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">Library</CardTitle>
                  <CardDescription>Issued books & fines</CardDescription>
                </CardHeader>
              </Card>

              {/* LMS - Learning */}
              <Card data-testid="lms-card" className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-indigo-200 bg-indigo-50/30" onClick={() => navigate('/student/lms')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Laptop className="w-6 h-6 text-indigo-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">LMS Learning</CardTitle>
                  <CardDescription>Course content, videos, assignments & announcements</CardDescription>
                </CardHeader>
              </Card>

              {/* Placement - Jobs */}
              <Card data-testid="jobs-card" className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-green-200 bg-green-50/30" onClick={() => navigate('/student/jobs')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <GraduationCap className="w-6 h-6 text-green-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">Job Opportunities</CardTitle>
                  <CardDescription>Campus placements & apply for jobs</CardDescription>
                </CardHeader>
              </Card>

              {/* Messages */}
              <Card data-testid="messages-card" className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-purple-200 bg-purple-50/30" onClick={() => navigate('/student/messages')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-purple-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">Messages</CardTitle>
                  <CardDescription>Chat with teachers and staff</CardDescription>
                </CardHeader>
              </Card>

              {/* Progress Dashboard */}
              <Card data-testid="progress-card" className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-emerald-200 bg-emerald-50/30" onClick={() => navigate('/student/progress')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Award className="w-6 h-6 text-emerald-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">My Progress</CardTitle>
                  <CardDescription>Track your learning journey</CardDescription>
                </CardHeader>
              </Card>

              {/* Profile */}
              <Card data-testid="profile-card" className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/student/profile')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <User className="w-6 h-6 text-gray-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">Profile</CardTitle>
                  <CardDescription>View your profile details</CardDescription>
                </CardHeader>
              </Card>

              {/* Change Password */}
              <Card data-testid="change-password-card" className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/student/change-password')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <Lock className="w-6 h-6 text-red-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <CardTitle className="text-lg mt-3">Change Password</CardTitle>
                  <CardDescription>Update your login password</CardDescription>
                </CardHeader>
              </Card>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
